import { z } from 'zod';
import { AskTheWireBudgetSchema, AskTheWireEvidenceSchema, ASK_WIRE_DEGRADE_REASONS } from './ask-the-wire';

/**
 * SITREP-FROM-SIGNALS (#300) — the request/result contract for the council's
 * build-THIRD "share" seam (docs/news-llm-engine-council-2026-06-15.md, Concept
 * D; PRD §4.19 Public-Information + §4.44 on-demand inference). On operator
 * demand, a Haiku-class model drafts the PUBLIC-INFORMATION section of a SITREP
 * for a tracked incident, GROUNDED ONLY in a BOUNDED retrieval of recent signals
 * + briefs + anomalies in the incident's focus — an EDITABLE, CITED paragraph the
 * operator folds into the real report and OWNS. The operator is the author of
 * record: the draft is NEVER auto-inserted into a SITREP, NEVER auto-forwarded as
 * fact, NEVER auto-urgent, and NEVER creates an anomaly.
 *
 * It reuses the shipped on-demand seam wholesale: the same injectable client, the
 * same cite-or-abstain enforced IN CODE (an answer that cites nothing, or whose
 * §3.7 grounding gate catches an un-sourced place, never reaches the operator),
 * the same ask_the_wire_queries cost cage (per-operator/day cap + global daily $
 * ceiling, discriminated by a `kind` column) PLUS a per-incident cooldown so a
 * single incident cannot be re-drafted in a tight loop. On a cap hit, a cooldown,
 * a thin signal set, or any failure it DEGRADES/abstains — never a dead box.
 *
 * The audited act is the DRAFT itself: a `draft_media_sitrep` operator-action row
 * (distinct from generate_sitrep — this writes no SITREP, only a candidate
 * paragraph the operator may discard).
 */

// ── Request ──────────────────────────────────────────────────────────────────

export const SitrepDraftRequestSchema = z.object({
  /** The tracked incident this public-info draft is for. Anchors the cooldown +
   *  the retrieval focus (the incident centroid → bbox + its hazard). */
  incident_id: z.string().uuid(),
  /** Freshness window in hours over the wire. Default 48h; cap 336h (2 weeks). */
  hours: z.coerce.number().min(1).max(336).optional().default(48),
  /** Optional operator steer ("what should the public know about evacuations?")
   *  — biases the retrieval rank + the prose focus. Never geocoded; bounded. */
  focus: z.string().trim().max(200).optional(),
  /** Client session id for the operator-action audit trail. */
  session_id: z.string().min(1).max(128).optional(),
});
export type SitrepDraftRequest = z.infer<typeof SitrepDraftRequestSchema>;

// ── Result ───────────────────────────────────────────────────────────────────

/** drafted = a grounded, cited public-info paragraph the operator can edit + fold
 *  in. abstained = nothing in the wire supports a public-info statement for this
 *  incident (deterministic on an empty set, or the model declined). degraded = a
 *  cap hit / failure / §3.7 withhold fell back to the plain list. cooled = a draft
 *  was generated for this incident within the cooldown window (re-draft later). */
export const SITREP_DRAFT_STATUSES = ['drafted', 'abstained', 'degraded', 'cooled'] as const;
export type SitrepDraftStatus = (typeof SITREP_DRAFT_STATUSES)[number];

export const SitrepDraftResultSchema = z.object({
  status: z.enum(SITREP_DRAFT_STATUSES),
  /** The editable, cited public-info prose with `[n]` markers — drafted only;
   *  null on abstain/degrade/cooled. The operator EDITS and OWNS this; it is not a
   *  SITREP and is never auto-inserted. */
  draft: z.string().nullable(),
  abstained: z.boolean(),
  degraded: z.boolean(),
  cooled: z.boolean(),
  /** Why a degraded result fell back (null unless degraded). Reuses the cost-cage
   *  / honesty reasons; a cooldown is its own status, not a degrade reason. */
  degradeReason: z.enum(ASK_WIRE_DEGRADE_REASONS).nullable(),
  /** The always-present honesty line — never presented as confirmed fact. */
  notConfirmedLine: z.string(),
  /** "AI-generated draft · N sources" provenance chrome (§3.6). */
  provenanceLine: z.string(),
  /** The numbered retrieval set (model input + degrade-to-list). May be empty. */
  evidence: z.array(AskTheWireEvidenceSchema),
  /** Ids within `evidence` the draft actually cited (drafted only; else []). */
  citationIds: z.array(z.string()),
  retrievedCount: z.number().int().nonnegative(),
  /** Which model wrote the draft (null on a free abstain / degrade / cooldown). */
  model: z.string().nullable(),
  /** This draft's billed cost in USD (0 on a free abstain / degrade / cooldown). */
  costUsd: z.number().nonnegative(),
  budget: AskTheWireBudgetSchema,
  /** Minutes until this incident may be re-drafted (cooled only; else null). */
  cooldownRetryMinutes: z.number().int().nonnegative().nullable(),
  /** The incident header label echoed for the UI ("Name (INC-NNNNN)"). */
  incidentLabel: z.string(),
});
export type SitrepDraftResult = z.infer<typeof SitrepDraftResultSchema>;
